<div class="rgm" cdkDropListGroup>

  <!-- Pivot-layout toolbar ─────────────────────────────────────── -->
  <div class="rgm-layout" *ngIf="!hideLayoutBins && (characteristics || []).length >= 2">
    <div class="rgm-layout-bin">
      <span class="rgm-layout-label" i18n="@@Rows">Rows</span>
      <div class="rgm-layout-chips"
           cdkDropList
           [id]="rowBinId"
           [cdkDropListData]="effectiveRowAxes"
           cdkDropListOrientation="horizontal"
           (cdkDropListDropped)="onAxisDrop($event)">
        <div class="rgm-layout-chip" *ngFor="let c of effectiveRowAxes"
             cdkDrag [cdkDragDisabled]="readOnly">
          <mat-icon class="rgm-layout-chip-handle">drag_indicator</mat-icon>
          <span>{{characteristicLabel(c)}}</span>
        </div>
        <span class="rgm-layout-empty" *ngIf="effectiveRowAxes.length === 0"
              i18n="@@(drop characteristics here)">(drop characteristics here)</span>
      </div>
    </div>

    <button mat-icon-button class="rgm-layout-swap"
            (click)="swapAxes()" [disabled]="readOnly"
            i18n-aria-label="@@Swap rows and columns" aria-label="Swap rows and columns"
            i18n-matTooltip="@@Swap rows and columns" matTooltip="Swap rows and columns">
      <mat-icon>swap_horiz</mat-icon>
    </button>

    <div class="rgm-layout-bin">
      <span class="rgm-layout-label" i18n="@@Columns">Columns</span>
      <div class="rgm-layout-chips"
           cdkDropList
           [id]="colBinId"
           [cdkDropListData]="effectiveColAxes"
           cdkDropListOrientation="horizontal"
           (cdkDropListDropped)="onAxisDrop($event)">
        <div class="rgm-layout-chip" *ngFor="let c of effectiveColAxes"
             cdkDrag [cdkDragDisabled]="readOnly">
          <mat-icon class="rgm-layout-chip-handle">drag_indicator</mat-icon>
          <span>{{characteristicLabel(c)}}</span>
        </div>
        <span class="rgm-layout-empty" *ngIf="effectiveColAxes.length === 0"
              i18n="@@(drop characteristics here)">(drop characteristics here)</span>
      </div>
    </div>
  </div>

  <!-- Pivot table ─────────────────────────────────────────────── -->
  <div class="rgm-table-scroll" *ngIf="rowPaths.length > 0 && colPaths.length > 0">
  <table class="rgm-table">
    <thead>
      <!-- One header row per col-axis level. Outer levels use colspan. -->
      <tr *ngFor="let headerRow of colHeaderRows; let lvl = index">
        <!-- Spacer cells for the row-header columns. The first column-header row
             emits a single corner cell that spans all col-header rows AND all
             row-header columns. Subsequent col-header rows emit nothing in the
             corner (covered by rowspan on the first). -->
        <th *ngIf="lvl === 0" class="rgm-corner"
            [attr.colspan]="effectiveRowAxes.length || 1"
            [attr.rowspan]="colHeaderRows.length">
          <span class="rgm-corner-inner">
            <!-- One small `+`/`−` toggle per characteristic, next to its
                 name. Clicking it adds (or removes, when not pinned by an
                 authored cell) a `-` slot on that axis. The `-` slot
                 sits alongside the literals + `*`; cells in it are
                 authored independently per row/col, letting the user
                 express "specific value on the other axes, opt-out on
                 this one" combos (e.g. `GRAY|-`). -->
            <span class="rgm-corner-row" *ngFor="let c of effectiveRowAxes">
              <mat-icon>view_stream</mat-icon>{{characteristicLabel(c)}}
              <button mat-icon-button class="rgm-char-collapse-toggle"
                      [disabled]="readOnly"
                      (click)="toggleCharacteristicCollapseSlot(c)"
                      [matTooltip]="characteristicCollapseTooltip(c)"
                      [class.rgm-char-collapse-on]="hasCollapsedSlot(c)"
                      [class.rgm-char-collapse-pinned]="hasCollapsedSlot(c) && !canHideCollapsedSlot(c)">
                <mat-icon>{{characteristicCollapseIcon(c)}}</mat-icon>
              </button>
            </span>
            <span class="rgm-corner-col" *ngFor="let c of effectiveColAxes">
              <mat-icon>view_column</mat-icon>{{characteristicLabel(c)}}
              <button mat-icon-button class="rgm-char-collapse-toggle"
                      [disabled]="readOnly"
                      (click)="toggleCharacteristicCollapseSlot(c)"
                      [matTooltip]="characteristicCollapseTooltip(c)"
                      [class.rgm-char-collapse-on]="hasCollapsedSlot(c)"
                      [class.rgm-char-collapse-pinned]="hasCollapsedSlot(c) && !canHideCollapsedSlot(c)">
                <mat-icon>{{characteristicCollapseIcon(c)}}</mat-icon>
              </button>
            </span>
          </span>
        </th>
        <!-- Column headers — one per col-axis value (literals, `*`, and
             optionally `-`). No per-header toggle: collapse is a
             per-characteristic decision, taken from the corner cell
             above. -->
        <th *ngFor="let h of headerRow"
            [attr.colspan]="h.span"
            class="rgm-col-head"
            [class.rgm-col-head-outer]="h.span > 1"
            [class.rgm-col-head-collapsed]="h.value === '-'"
            [matTooltip]="h.tooltip || ''"
            [matTooltipDisabled]="!h.tooltip"
            matTooltipClass="rgm-tooltip-multiline">
          <span class="rgm-head-label">{{labelForHeader(h)}}</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let rowPath of rowPaths; let ri = index">
        <!-- Row-header cells with rowspan. No per-header toggle here —
             same rationale as the column headers above. -->
        <th *ngFor="let h of rowHeaderCells[ri]"
            [attr.rowspan]="h.span"
            class="rgm-row-head"
            [class.rgm-row-head-outer]="h.span > 1"
            [class.rgm-row-head-collapsed]="h.value === '-'"
            [matTooltip]="h.tooltip || ''"
            [matTooltipDisabled]="!h.tooltip"
            matTooltipClass="rgm-tooltip-multiline">
          <span class="rgm-head-label">{{labelForHeader(h)}}</span>
        </th>

        <!-- Cell inputs, one per column path. Cells whose row- or col-path
             contains a `-` (collapsed) token are still fully editable —
             the `-` only means "this axis position doesn't differentiate
             at this group level", not "this cell is locked". The cell
             background gets a subtle tint via `.rgm-cell-collapsed` so
             the user can tell the cell came from a collapsed-axis row
             or column. -->
        <td *ngFor="let colPath of colPaths"
            [class.rgm-cell-infeasible]="isEffectivelyInfeasible(rowPath, colPath)"
            [class.rgm-cell-collapsed]="isCollapsedPath(rowPath, colPath)"
            [class.rgm-cell-shadowed]="isShadowed(rowPath, colPath)"
            [class.rgm-cell-inherited]="!isAuthoredExact(rowPath, colPath) && !isShadowed(rowPath, colPath) && !isCollapsedKey(rowPath, colPath)">
          <ng-container *ngIf="!isShadowed(rowPath, colPath); else shadowedCell">
            <!-- Cells whose composed key carries `-` at one or more
                 positions are gated: until the user explicitly enables
                 them, only an "enable" button appears. This makes the
                 difference between "no value" and "authored 0" obvious,
                 and is the only way to author a literal `0` in a `-`
                 slot (since the legacy `0`-default would otherwise
                 swallow that intent). Once enabled the standard
                 amount-input + infeasible toggle appear, plus a small
                 "remove" button to revert the cell to unauthored. -->
            <ng-container *ngIf="isCollapsedKey(rowPath, colPath); else normalCellBody">
              <ng-container *ngIf="isAuthored(rowPath, colPath); else enableCollapsed">
                <input class="rgm-input" type="text" inputmode="decimal"
                       *ngIf="!isInfeasible(cellAt(rowPath, colPath))"
                       [value]="displayAmount(cellAt(rowPath, colPath), true)"
                       [disabled]="readOnly"
                       (change)="onAmountInput(rowPath, colPath, $any($event.target).value)"/>
                <button mat-icon-button class="rgm-cell-action" [disabled]="readOnly"
                        (click)="onInfeasibleClick(rowPath, colPath)"
                        i18n-matTooltip="@@Toggle infeasible" matTooltip="Toggle infeasible">
                  <mat-icon *ngIf="!isInfeasible(cellAt(rowPath, colPath))">check_circle_outline</mat-icon>
                  <mat-icon *ngIf="isInfeasible(cellAt(rowPath, colPath))" class="rgm-infeasible-icon">block</mat-icon>
                </button>
                <button mat-icon-button class="rgm-cell-action rgm-cell-disable"
                        [disabled]="readOnly"
                        (click)="disableCollapsedCell(rowPath, colPath)"
                        i18n-matTooltip="@@Remove this — (collapsed) cell"
                        matTooltip="Remove this — (collapsed) cell">
                  <mat-icon>close</mat-icon>
                </button>
              </ng-container>
              <ng-template #enableCollapsed>
                <button mat-icon-button class="rgm-cell-action rgm-cell-enable"
                        [disabled]="readOnly"
                        (click)="enableCollapsedCell(rowPath, colPath)"
                        i18n-matTooltip="@@Enable this — (collapsed) cell to author a value"
                        matTooltip="Enable this — (collapsed) cell to author a value">
                  <mat-icon>add_circle_outline</mat-icon>
                </button>
              </ng-template>
            </ng-container>
            <ng-template #normalCellBody>
              <!-- Non-`-` cell. The input is ALWAYS rendered (single
                   click → type → tab to commit), so the most common
                   editing path is fast. Unauthored cells show the
                   value the ENGINE would resolve for this coordinate
                   as a placeholder — either a wider wildcard's amount
                   or `∞` when a wider wildcard is infeasible — so the
                   user can tell at a glance what the cell will
                   contribute, but typing a real number IMMEDIATELY
                   materializes an authored cell. Eliminates the
                   previous two-click pencil dance.

                   Inherited-infeasible cells still show the
                   block-icon toggle pre-flipped so it's obvious the
                   cell currently resolves to `∞`; clicking it (or
                   typing a value) drops back to a feasible authored
                   cell. -->
              <ng-container *ngIf="effectiveDisplay(rowPath, colPath) as eff">
                <input class="rgm-input" type="text" inputmode="decimal"
                       *ngIf="!isEffectivelyInfeasible(rowPath, colPath)"
                       [class.rgm-input-inherited]="!isAuthoredExact(rowPath, colPath)"
                       [value]="isAuthoredExact(rowPath, colPath) ? displayAmount(cellAt(rowPath, colPath)) : ''"
                       [attr.placeholder]="isAuthoredExact(rowPath, colPath) ? null : eff.text"
                       [disabled]="readOnly"
                       i18n-matTooltip="@@Inherited from a wider wildcard cell. Type a value to override."
                       [matTooltip]="!isAuthoredExact(rowPath, colPath) ? 'Inherited from a wider wildcard cell. Type a value to override.' : ''"
                       [matTooltipDisabled]="isAuthoredExact(rowPath, colPath)"
                       (change)="onAmountInput(rowPath, colPath, $any($event.target).value)"/>
                <!-- Inherited-infeasible state: render an "∞" stand-in
                     in place of the input, plus the same toggle the
                     authored path uses so a single click brings the
                     cell back to a feasible amount-input. -->
                <span class="rgm-inherited-infinity"
                      *ngIf="isEffectivelyInfeasible(rowPath, colPath) && !isAuthoredExact(rowPath, colPath)"
                      i18n-matTooltip="@@Inherited infeasible from a wider wildcard cell. Click the icon (or this ∞) to override."
                      matTooltip="Inherited infeasible from a wider wildcard cell. Click the icon (or this ∞) to override."
                      (click)="materializeInherited(rowPath, colPath)">∞</span>
                <button mat-icon-button class="rgm-cell-action" [disabled]="readOnly"
                        (click)="onInfeasibleClick(rowPath, colPath)"
                        i18n-matTooltip="@@Toggle infeasible" matTooltip="Toggle infeasible">
                  <mat-icon *ngIf="!isEffectivelyInfeasible(rowPath, colPath)">check_circle_outline</mat-icon>
                  <mat-icon *ngIf="isEffectivelyInfeasible(rowPath, colPath)" class="rgm-infeasible-icon">block</mat-icon>
                </button>
              </ng-container>
            </ng-template>
          </ng-container>
          <ng-template #shadowedCell>
            <span class="rgm-cell-shadowed-marker"
                  i18n-matTooltip="@@Covered by a — (collapsed) cell on this slice"
                  matTooltip="Covered by a — (collapsed) cell on this slice">·</span>
          </ng-template>
        </td>
      </tr>
    </tbody>
  </table>
  </div>

  <!-- Edge cases ─────────────────────────────────────────────── -->
  <div *ngIf="characteristics.length === 0" class="rgm-empty">
    <mat-icon>info_outline</mat-icon>
    <span i18n="@@No characteristics — pick at least one to see the matrix.">No characteristics — pick at least one to see the matrix.</span>
  </div>

</div>


